import { useState } from 'react'

import { useToasts } from '@/hooks/useToasts'

import { ApolloError, useApolloClient } from '@apollo/client'

import { getDeviceConnection } from '@/features/device-crud'
import {
  useCreateTailgateConnMutation,
  useDeviceAssocationOptionsQuery,
  useModifyTailgateConnMutation,
} from '@/graphql/generated/hooks'
import { AssociatedDevicesQuery } from '@/graphql/generated/operations'
import { DeviceType } from '@/graphql/generated/schemas'
import { includeIfActive } from '@/utils/apollo'

import { AssociatedDeviceRow } from '../types/types'

const DEVICE_ASSOCIATION_REFETCH_QUERIES = [
  'AssociatedDevices',
  'DeviceAssocationOptions',
]

interface UseAssociatedDeviceActionsIProps {
  deviceId: string
  deviceType: DeviceType
  deviceFloorId?: string | null
  associatedDevicesData: AssociatedDevicesQuery | undefined
  enabled: boolean
}

export const useAssociatedDeviceActions = ({
  deviceId,
  deviceType,
  deviceFloorId,
  associatedDevicesData,
  enabled,
}: UseAssociatedDeviceActionsIProps) => {
  const { showError, showSuccess } = useToasts()
  const client = useApolloClient()
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [isAdding, setIsAdding] = useState(false)

  const isCamera = deviceType === DeviceType.Camera
  const deviceTypes = isCamera
    ? [DeviceType.Door, DeviceType.Alarm]
    : [DeviceType.Camera]

  const { data: optionsData } = useDeviceAssocationOptionsQuery({
    skip: !deviceFloorId || !enabled,
    variables: {
      filter: {
        floorIdIn: [deviceFloorId!],
        typeIn: deviceTypes,
      },
      id: deviceId,
    },
  })

  // The Devices table stays mounted behind the drawer and reads its
  // Associations column from DeviceAssociationCounts. The mutations do not
  // return cameras.totalCount, so the count only refreshes through a refetch.
  const refetchQueries = () => [
    ...DEVICE_ASSOCIATION_REFETCH_QUERIES,
    ...includeIfActive(client, 'DeviceAssociationCounts'),
  ]

  const [createTailgateConn] = useCreateTailgateConnMutation({
    awaitRefetchQueries: true,
    refetchQueries,
  })

  const [modifyTailgateConn] = useModifyTailgateConnMutation({
    awaitRefetchQueries: true,
    refetchQueries,
  })

  const addDevice = async (selectedDeviceId: string) => {
    setIsAdding(true)
    try {
      if (isCamera) {
        // Look in optionsData.devices (the floor's doors/alarms) to find if the
        // selected door already has a connection, so so modify the connection with the new camera
        const existingConn = optionsData?.devices?.edges
          ?.flatMap(
            (e) => e?.node?.tailgateConns?.edges?.map((te) => te?.node) ?? []
          )
          ?.find((conn) => conn?.door?.id === selectedDeviceId)
        if (existingConn) {
          await modifyTailgateConn({
            variables: {
              input: {
                id: existingConn.id,
                cameraIdsToAdd: [deviceId],
                cameraIdsToRemove: [],
              },
            },
          })
        } else {
          await createTailgateConn({
            variables: {
              input: { cameraIds: [deviceId], doorId: selectedDeviceId },
            },
          })
        }
      } else {
        // Use optionsData (which includes tailgateConns for this device) to
        // check if a conn already exists before deciding to modify or create.
        const existingConn = getDeviceConnection(optionsData, deviceId)
        if (existingConn) {
          await modifyTailgateConn({
            variables: {
              input: {
                id: existingConn.id,
                cameraIdsToAdd: [selectedDeviceId],
                cameraIdsToRemove: [],
              },
            },
          })
        } else {
          await createTailgateConn({
            variables: {
              input: { cameraIds: [selectedDeviceId], doorId: deviceId },
            },
          })
        }
      }
      showSuccess('Device association added')
    } catch (e) {
      showError(e as ApolloError)
    } finally {
      setIsAdding(false)
    }
  }

  const removeDevice = async (row: AssociatedDeviceRow) => {
    setRemovingId(row.id)
    try {
      const cameraIdToRemove = isCamera ? deviceId : row.id
      await modifyTailgateConn({
        variables: {
          input: {
            id: row.connectionId,
            cameraIdsToAdd: [],
            cameraIdsToRemove: [cameraIdToRemove],
          },
        },
      })
      showSuccess('Device association removed')
    } catch (e) {
      showError(e as ApolloError)
    } finally {
      setRemovingId(null)
    }
  }

  const alreadyAssociatedIds = (
    associatedDevicesData?.device?.tailgateConns?.edges ?? []
  ).flatMap((edge) => {
    const conn = edge?.node
    if (isCamera) {
      return conn?.door?.id ? [conn.door.id] : []
    }
    return (conn?.cameras?.edges ?? []).flatMap((ce) =>
      ce?.node?.id ? [ce.node.id] : []
    )
  })

  const availableOptions = (optionsData?.devices?.edges ?? [])
    .flatMap((edge) => {
      const node = edge?.node
      if (!node?.id || alreadyAssociatedIds.includes(node.id)) return []
      return [{ label: node.name ?? node.id, value: node.id }]
    })
    .sort((a, b) => a.label.localeCompare(b.label))

  return {
    availableOptions,
    addDevice,
    removeDevice,
    isAdding,
    removingId,
  }
}
